import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, inject, INJECTOR, runInInjectionContext, Injectable, PLATFORM_ID, NgZone, DestroyRef, ENVIRONMENT_INITIALIZER, makeEnvironmentProviders, SecurityContext, LOCALE_ID, Component, Inject, Optional, Input, Directive, HostListener, NgModule } from '@angular/core';
import { Subject, fromEvent, merge } from 'rxjs';
import * as i1 from '@angular/platform-browser';
function coerceErrorHandler(errorOrHandler) {
  return typeof errorOrHandler === 'string' ? () => {
    throw new Error(errorOrHandler);
  } : errorOrHandler;
}
function runOnce(fn, errorOrHandler) {
  const errorHandler = errorOrHandler ? coerceErrorHandler(errorOrHandler) : () => undefined;
  let run = false;
  return (...args) => {
    if (run) {
      return errorHandler(...args);
    }
    run = true;
    return fn(...args);
  };
}
function appendTrailingSlash(str) {
  return str.endsWith('/') ? str : `${str}/`;
}
const createDefaultMatomoScriptElement = (scriptUrl, document) => {
  const g = document.createElement('script');
  g.type = 'text/javascript';
  g.defer = true;
  g.async = true;
  g.src = scriptUrl;
  return g;
};
const MATOMO_SCRIPT_FACTORY = new InjectionToken('MATOMO_SCRIPT_FACTORY', {
  providedIn: 'root',
  factory: () => createDefaultMatomoScriptElement
});
function requireNonNull(value, message) {
  if (value === null || value === undefined) {
    throw new Error('Unexpected ' + value + ' value: ' + message);
  }
  return value;
}
/** Coerce a data-bound value to a boolean */
function coerceCssSizeBinding(value) {
  if (value == null) {
    return '';
  }
  return typeof value === 'string' ? value : `${value}px`;
}
let ScriptInjector = /*#__PURE__*/(() => {
  class ScriptInjector {
    constructor() {
      this.scriptFactory = inject(MATOMO_SCRIPT_FACTORY);
      this.injector = inject(INJECTOR);
      this.document = inject(DOCUMENT);
    }
    injectDOMScript(scriptUrl) {
      const scriptElement = runInInjectionContext(this.injector, () => this.scriptFactory(scriptUrl, this.document));
      const selfScript = requireNonNull(this.document.getElementsByTagName('script')[0], 'no existing script found');
      const parent = requireNonNull(selfScript.parentNode, "no script's parent node found");
      parent.insertBefore(scriptElement, selfScript);
    }
    static {
      this.ɵfac = function ScriptInjector_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ScriptInjector)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ScriptInjector,
        factory: ScriptInjector.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ScriptInjector;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const CONFIG_NOT_FOUND = 'No Matomo configuration found! Have you included Matomo module using MatomoModule.forRoot() or provideMatomo()?';
/** Internal marker token to detect that router has been enabled */
const MATOMO_ROUTER_ENABLED = new InjectionToken('MATOMO_ROUTER_ENABLED', {
  factory() {
    return false;
  }
});
/** Injection token for {@link MatomoConfiguration} */
const MATOMO_CONFIGURATION = new InjectionToken('MATOMO_CONFIGURATION');
/**
 * For internal use only. Injection token for {@link InternalMatomoConfiguration}
 *
 */
const INTERNAL_MATOMO_CONFIGURATION$1 = new InjectionToken('INTERNAL_MATOMO_CONFIGURATION', {
  factory() {
    const {
      mode,
      requireConsent,
      ...restConfig
    } = requireNonNull(inject(MATOMO_CONFIGURATION, {
      optional: true
    }), CONFIG_NOT_FOUND);
    return {
      mode: mode ? coerceInitializationMode(mode) : undefined,
      disabled: false,
      enableLinkTracking: true,
      trackAppInitialLoad: !inject(MATOMO_ROUTER_ENABLED),
      requireConsent: requireConsent ? coerceConsentRequirement(requireConsent) : 'none',
      enableJSErrorTracking: false,
      runOutsideAngularZone: false,
      disableCampaignParameters: false,
      acceptDoNotTrack: false,
      ...restConfig
    };
  }
});
/**
 * For internal use only. Injection token for deferred {@link InternalMatomoConfiguration}.
 *
 */
const DEFERRED_INTERNAL_MATOMO_CONFIGURATION = new InjectionToken('DEFERRED_INTERNAL_MATOMO_CONFIGURATION', {
  factory: () => {
    const base = inject(INTERNAL_MATOMO_CONFIGURATION$1);
    let resolveFn;
    const configuration = new Promise(resolve => resolveFn = resolve);
    return {
      configuration,
      markReady(configuration) {
        requireNonNull(resolveFn, 'resolveFn')({
          ...base,
          ...configuration
        });
      }
    };
  }
});
/**
 * For internal use only. Injection token for fully loaded async {@link InternalMatomoConfiguration}.
 *
 */
const ASYNC_INTERNAL_MATOMO_CONFIGURATION = new InjectionToken('ASYNC_INTERNAL_MATOMO_CONFIGURATION', {
  factory: () => inject(DEFERRED_INTERNAL_MATOMO_CONFIGURATION).configuration
});
/** @deprecated Use {@link MatomoInitializationBehavior} instead */
var MatomoInitializationMode = /*#__PURE__*/function (MatomoInitializationMode) {
  /**
   * Automatically inject matomo script using provided configuration
   *
   * @deprecated Use `'auto'` instead
   */
  MatomoInitializationMode[MatomoInitializationMode["AUTO"] = 0] = "AUTO";
  /**
   * Do not inject Matomo script. In this case, initialization script must be provided
   *
   * @deprecated Use `'manual'` instead
   */
  MatomoInitializationMode[MatomoInitializationMode["MANUAL"] = 1] = "MANUAL";
  /**
   * Automatically inject matomo script when deferred tracker configuration is provided using `MatomoInitializerService.initializeTracker()`.
   *
   * @deprecated Use `'deferred'` instead
   */
  MatomoInitializationMode[MatomoInitializationMode["AUTO_DEFERRED"] = 2] = "AUTO_DEFERRED";
  return MatomoInitializationMode;
}(MatomoInitializationMode || {});
function coerceInitializationMode(value) {
  switch (value) {
    case MatomoInitializationMode.AUTO:
      return 'auto';
    case MatomoInitializationMode.MANUAL:
      return 'manual';
    case MatomoInitializationMode.AUTO_DEFERRED:
      return 'deferred';
    default:
      return value;
  }
}
/** @deprecated Use {@link MatomoConsentRequirement} instead */
var MatomoConsentMode = /*#__PURE__*/function (MatomoConsentMode) {
  /** Do not require any consent, always track users */
  MatomoConsentMode[MatomoConsentMode["NONE"] = 0] = "NONE";
  /** Require cookie consent */
  MatomoConsentMode[MatomoConsentMode["COOKIE"] = 1] = "COOKIE";
  /** Require tracking consent */
  MatomoConsentMode[MatomoConsentMode["TRACKING"] = 2] = "TRACKING";
  return MatomoConsentMode;
}(MatomoConsentMode || {});
function coerceConsentRequirement(value) {
  switch (value) {
    case MatomoConsentMode.NONE:
      return 'none';
    case MatomoConsentMode.COOKIE:
      return 'cookie';
    case MatomoConsentMode.TRACKING:
      return 'tracking';
    default:
      return value;
  }
}
function isAutoConfigurationMode(config) {
  return config.mode == null || config.mode === 'auto' || config.mode === MatomoInitializationMode.AUTO;
}
function hasMainTrackerConfiguration(config) {
  // If one is undefined, both should be
  return config.siteId != null && config.trackerUrl != null;
}
function isEmbeddedTrackerConfiguration(config) {
  return config.scriptUrl != null && !hasMainTrackerConfiguration(config);
}
function isExplicitTrackerConfiguration(config) {
  return hasMainTrackerConfiguration(config) || isMultiTrackerConfiguration(config);
}
function isMultiTrackerConfiguration(config) {
  return Array.isArray(config.trackers);
}
function getTrackersConfiguration(config) {
  return isMultiTrackerConfiguration(config) ? config.trackers : [{
    trackerUrl: config.trackerUrl,
    siteId: config.siteId,
    trackerUrlSuffix: config.trackerUrlSuffix
  }];
}
function initializeMatomoHolder() {
  window._paq = window._paq || [];
}
function trimTrailingUndefinedElements(array) {
  const trimmed = [...array];
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === undefined) {
    trimmed.pop();
  }
  return trimmed;
}
function createInternalMatomoTracker() {
  const disabled = inject(INTERNAL_MATOMO_CONFIGURATION$1).disabled;
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  return disabled || !isBrowser ? new NoopMatomoTracker() : new InternalMatomoTracker();
}
let InternalMatomoTracker = /*#__PURE__*/(() => {
  class InternalMatomoTracker {
    constructor() {
      this.ngZone = inject(NgZone);
      this.config = inject(INTERNAL_MATOMO_CONFIGURATION$1);
      initializeMatomoHolder();
    }
    /** Asynchronously call provided method name on matomo tracker instance */
    get(getter) {
      return this.pushFn(matomo => matomo[getter]());
    }
    pushFn(fn) {
      return new Promise(resolve => {
        this.push([function () {
          resolve(fn(this));
        }]);
      });
    }
    push(args) {
      if (this.config.runOutsideAngularZone) {
        this.ngZone.runOutsideAngular(() => {
          window._paq.push(trimTrailingUndefinedElements(args));
        });
      } else {
        window._paq.push(trimTrailingUndefinedElements(args));
      }
    }
    static {
      this.ɵfac = function InternalMatomoTracker_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || InternalMatomoTracker)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: InternalMatomoTracker,
        factory: () => createInternalMatomoTracker(),
        providedIn: 'root'
      });
    }
  }
  return InternalMatomoTracker;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class NoopMatomoTracker {
  /** Asynchronously call provided method name on matomo tracker instance */
  async get(_) {
    return Promise.reject('MatomoTracker is disabled');
  }
  push(_) {
    // No-op
  }
  async pushFn(_) {
    return Promise.reject('MatomoTracker is disabled');
  }
}
const ALREADY_INJECTED_ERROR = 'Matomo trackers have already been initialized';
const ALREADY_INITIALIZED_ERROR = 'Matomo has already been initialized';
function isECommerceCategoryView(param) {
  return typeof param === 'object' && Object.keys(param).length === 1 && param.productCategory != null;
}
function isECommerceItemView(param) {
  return typeof param === 'object' && 'productSKU' in param;
}
let MatomoTracker = /*#__PURE__*/(() => {
  class MatomoTracker {
    constructor() {
      this.delegate = inject(InternalMatomoTracker);
      this._pageViewTracked = new Subject();
      this.pageViewTracked = this._pageViewTracked.asObservable();
      inject(DestroyRef).onDestroy(() => this._pageViewTracked.complete());
    }
    /**
     * Logs a visit to this page.
     *
     * @param [customTitle] Optional title of the visited page.
     */
    trackPageView(customTitle) {
      this.delegate.push(['trackPageView', customTitle]);
      this._pageViewTracked.next();
    }
    /**
     * Logs an event with an event category (Videos, Music, Games…), an event action (Play, Pause, Duration,
     * Add Playlist, Downloaded, Clicked…), and an optional event name and optional numeric value.
     *
     * @param category Category of the event.
     * @param action Action of the event.
     * @param [name] Optional name of the event.
     * @param [value] Optional value for the event.
     * @param [customData] Optional custom data for the event.
     */
    trackEvent(category, action, name, value, customData) {
      this.delegate.push(['trackEvent', category, action, name, value, customData]);
    }
    /**
     * Logs an internal site search for a specific keyword, in an optional category,
     * specifying the optional count of search results in the page.
     *
     * @param keyword Keywords of the search query.
     * @param [category] Optional category of the search query.
     * @param [resultsCount] Optional number of results returned by the search query.
     * @param [customData] Optional custom data for the search query.
     */
    trackSiteSearch(keyword, category, resultsCount, customData) {
      this.delegate.push(['trackSiteSearch', keyword, category, resultsCount, customData]);
    }
    /**
     * Manually logs a conversion for the numeric goal ID, with an optional numeric custom revenue customRevenue.
     *
     * @param idGoal numeric ID of the goal to log a conversion for.
     * @param [customRevenue] Optional custom revenue to log for the goal.
     * @param [customData] Optional custom data for the goal.
     */
    trackGoal(idGoal, customRevenue, customData) {
      this.delegate.push(['trackGoal', idGoal, customRevenue, customData]);
    }
    /**
     * Manually logs a click from your own code.
     *
     * @param url Full URL which is to be tracked as a click.
     * @param linkType Either 'link' for an outlink or 'download' for a download.
     * @param [customData] Optional custom data for the link.
     */
    trackLink(url, linkType, customData) {
      this.delegate.push(['trackLink', url, linkType, customData]);
    }
    /**
     * Scans the entire DOM for all content blocks and tracks all impressions once the DOM ready event has been triggered.
     *
     */
    trackAllContentImpressions() {
      this.delegate.push(['trackAllContentImpressions']);
    }
    /**
     * Scans the entire DOM for all content blocks as soon as the page is loaded.<br />
     * It tracks an impression only if a content block is actually visible.
     *
     * @param checkOnScroll If true, checks for new content blocks while scrolling the page.
     * @param timeInterval Duration, in milliseconds, between two checks upon scroll.
     */
    trackVisibleContentImpressions(checkOnScroll, timeInterval) {
      this.delegate.push(['trackVisibleContentImpressions', checkOnScroll, timeInterval]);
    }
    /**
     * Scans the given DOM node and its children for content blocks and tracks an impression for them
     * if no impression was already tracked for it.
     *
     * @param node DOM node in which to look for content blocks which have not been previously tracked.
     */
    trackContentImpressionsWithinNode(node) {
      this.delegate.push(['trackContentImpressionsWithinNode', node]);
    }
    /**
     * Tracks an interaction with the given DOM node/content block.
     *
     * @param node DOM node for which to track a content interaction.
     * @param contentInteraction Name of the content interaction.
     */
    trackContentInteractionNode(node, contentInteraction) {
      this.delegate.push(['trackContentInteractionNode', node, contentInteraction]);
    }
    /**
     * Tracks a content impression using the specified values.
     *
     * @param contentName Content name.
     * @param contentPiece Content piece.
     * @param contentTarget Content target.
     */
    trackContentImpression(contentName, contentPiece, contentTarget) {
      this.delegate.push(['trackContentImpression', contentName, contentPiece, contentTarget]);
    }
    /**
     * Tracks a content interaction using the specified values.
     *
     * @param contentInteraction Content interaction.
     * @param contentName Content name.
     * @param contentPiece Content piece.
     * @param contentTarget Content target.
     */
    trackContentInteraction(contentInteraction, contentName, contentPiece, contentTarget) {
      this.delegate.push(['trackContentInteraction', contentInteraction, contentName, contentPiece, contentTarget]);
    }
    /**
     * Logs all found content blocks within a page to the console. This is useful to debug / test content tracking.
     */
    logAllContentBlocksOnPage() {
      this.delegate.push(['logAllContentBlocksOnPage']);
    }
    /**
     * Send a ping request
     * <p>
     * Ping requests do not track new actions.
     * If they are sent within the standard visit length, they will update the existing visit time.
     * If sent after the standard visit length, ping requests will be ignored.
     * See also {@link #enableHeartBeatTimer enableHeartBeatTimer()}.
     *
     * @see enableHeartBeatTimer
     */
    ping() {
      this.delegate.push(['ping']);
    }
    /**
     * Install a Heart beat timer that will regularly send requests to Matomo in order to better measure the time spent on the page.<br />
     * These requests will be sent only when the user is actively viewing the page (when the tab is active and in focus).<br />
     * These requests will not track additional actions or page views.<br />
     * By default, the delay is set to 15 seconds.
     *
     * @param delay Delay, in seconds, between two heart beats to the server.
     */
    enableHeartBeatTimer(delay) {
      this.delegate.push(['enableHeartBeatTimer', delay]);
    }
    /**
     * Installs link tracking on all applicable link elements.
     *
     * @param usePseudoClickHandler Set to `true` to use pseudo click-handler (treat middle click and open contextmenu as
     * left click).<br />
     * A right click (or any click that opens the context menu) on a link will be tracked as clicked even if "Open in new tab"
     * is not selected.<br />
     * If "false" (default), nothing will be tracked on open context menu or middle click.
     */
    enableLinkTracking(usePseudoClickHandler = false) {
      this.delegate.push(['enableLinkTracking', usePseudoClickHandler]);
    }
    /** Disables page performance tracking */
    disablePerformanceTracking() {
      this.delegate.push(['disablePerformanceTracking']);
    }
    /**
     * Enables cross domain linking. By default, the visitor ID that identifies a unique visitor is stored in the browser's
     * first party cookies.<br />
     * This means the cookie can only be accessed by pages on the same domain.<br />
     * If you own multiple domains and would like to track all the actions and pageviews of a specific visitor into the same visit,
     * you may enable cross domain linking.<br />
     * Whenever a user clicks on a link it will append a URL parameter pk_vid to the clicked URL which forwards the current
     * visitor ID value to the page of the different domain.
     *
     */
    enableCrossDomainLinking() {
      this.delegate.push(['enableCrossDomainLinking']);
    }
    /**
     * By default, the two visits across domains will be linked together when the link is clicked and the page is loaded within
     * a 180 seconds timeout window.
     *
     * @param timeout Timeout, in seconds, between two actions across two domains before creating a new visit.
     */
    setCrossDomainLinkingTimeout(timeout) {
      this.delegate.push(['setCrossDomainLinkingTimeout', timeout]);
    }
    /**
     * Get the query parameter to append to links to handle cross domain linking.
     *
     * Use this to add cross domain support for links that are added to the DOM dynamically
     */
    getCrossDomainLinkingUrlParameter() {
      return this.delegate.get('getCrossDomainLinkingUrlParameter');
    }
    /**
     * Overrides document.title
     *
     * @param title Title of the document.
     */
    setDocumentTitle(title) {
      this.delegate.push(['setDocumentTitle', title]);
    }
    /**
     * Set array of hostnames or domains to be treated as local.<br />
     * For wildcard subdomains, you can use: `setDomains('.example.com')`; or `setDomains('*.example.com');`.<br />
     * You can also specify a path along a domain: `setDomains('*.example.com/subsite1');`.
     *
     * @param domains List of hostnames or domains, with or without path, to be treated as local.
     */
    setDomains(domains) {
      this.delegate.push(['setDomains', domains]);
    }
    /**
     * Override the page's reported URL.
     *
     * @param url URL to be reported for the page.
     */
    setCustomUrl(url) {
      this.delegate.push(['setCustomUrl', url]);
    }
    /**
     * Overrides the detected Http-Referer.
     *
     * @param url URL to be reported for the referer.
     */
    setReferrerUrl(url) {
      this.delegate.push(['setReferrerUrl', url]);
    }
    /**
     * Specifies the website ID.<br />
     * Redundant: can be specified in getTracker() constructor.
     *
     * @param siteId Site ID for the tracker.
     */
    setSiteId(siteId) {
      this.delegate.push(['setSiteId', siteId]);
    }
    /**
     * Specify the Matomo HTTP API URL endpoint. Points to the root directory of matomo,
     * e.g. http://matomo.example.org/ or https://example.org/matomo/.<br />
     * This function is only useful when the 'Overlay' report is not working.<br />
     * By default, you do not need to use this function.
     *
     * @param url URL for Matomo HTTP API endpoint.
     */
    setApiUrl(url) {
      this.delegate.push(['setApiUrl', url]);
    }
    /**
     * Specifies the Matomo server URL.<br />
     * Redundant: can be specified in getTracker() constructor.
     *
     * @param url URL for the Matomo server.
     */
    setTrackerUrl(url) {
      this.delegate.push(['setTrackerUrl', url]);
    }
    /**
     * Register an additional Matomo server<br />
     * Redundant: can be specified in getTracker() constructor.
     *
     * @param url URL for the Matomo server.
     * @param siteId Site ID for the tracker
     */
    addTracker(url, siteId) {
      this.delegate.push(['addTracker', url, siteId]);
    }
    /**
     * Returns the Matomo server URL.
     *
     * @returns Promise for the Matomo server URL.
     */
    getMatomoUrl() {
      return this.delegate.get('getMatomoUrl');
    }
    /** @deprecated use `getMatomoUrl` instead */
    getPiwikUrl() {
      return this.delegate.get('getPiwikUrl');
    }
    /**
     * Returns the current url of the page that is currently being visited.<br />
     * If a custom URL was set before calling this method, the custom URL will be returned.
     *
     * @returns Promise for the URL of the current page.
     */
    getCurrentUrl() {
      return this.delegate.get('getCurrentUrl');
    }
    /**
     * Set classes to be treated as downloads (in addition to piwik_download).
     *
     * @param classes Class, or list of classes to be treated as downloads.
     */
    setDownloadClasses(classes) {
      this.delegate.push(['setDownloadClasses', classes]);
    }
    /**
     * Set list of file extensions to be recognized as downloads.<br />
     * Example: `'docx'` or `['docx', 'xlsx']`.
     *
     * @param extensions Extension, or list of extensions to be recognized as downloads.
     */
    setDownloadExtensions(extensions) {
      this.delegate.push(['setDownloadExtensions', extensions]);
    }
    /**
     * Set additional file extensions to be recognized as downloads.<br />
     * Example: `'docx'` or `['docx', 'xlsx']`.
     *
     * @param extensions Extension, or list of extensions to be recognized as downloads.
     */
    addDownloadExtensions(extensions) {
      this.delegate.push(['addDownloadExtensions', extensions]);
    }
    /**
     * Set file extensions to be removed from the list of download file extensions.<br />
     * Example: `'docx'` or `['docx', 'xlsx']`.
     *
     * @param extensions Extension, or list of extensions not to be recognized as downloads.
     */
    removeDownloadExtensions(extensions) {
      this.delegate.push(['removeDownloadExtensions', extensions]);
    }
    /**
     * Set classes to be ignored if present in link (in addition to piwik_ignore).
     *
     * @param classes Class, or list of classes to be ignored if present in link.
     */
    setIgnoreClasses(classes) {
      this.delegate.push(['setIgnoreClasses', classes]);
    }
    /**
     * Set classes to be treated as outlinks (in addition to piwik_link).
     *
     * @param classes Class, or list of classes to be treated as outlinks.
     */
    setLinkClasses(classes) {
      this.delegate.push(['setLinkClasses', classes]);
    }
    /**
     * Set delay for link tracking (in milliseconds).
     *
     * @param delay Delay, in milliseconds, for link tracking.
     */
    setLinkTrackingTimer(delay) {
      this.delegate.push(['setLinkTrackingTimer', delay]);
    }
    /**
     * Returns delay for link tracking.
     *
     * @returns Promise for the delay in milliseconds.
     */
    getLinkTrackingTimer() {
      return this.delegate.get('getLinkTrackingTimer');
    }
    /**
     * Set to true to not record the hash tag (anchor) portion of URLs.
     *
     * @param value If true, the hash tag portion of the URLs won't be recorded.
     */
    discardHashTag(value) {
      this.delegate.push(['discardHashTag', value]);
    }
    /**
     * By default, Matomo uses the browser DOM Timing API to accurately determine the time it takes to generate and download
     * the page. You may overwrite this value with this function.
     *
     * <b>This feature has been deprecated since Matomo 4. Any call will be ignored with Matomo 4. Use {@link setPagePerformanceTiming setPagePerformanceTiming()} instead.</b>
     *
     * @param generationTime Time, in milliseconds, of the page generation.
     */
    setGenerationTimeMs(generationTime) {
      this.delegate.push(['setGenerationTimeMs', generationTime]);
    }
    setPagePerformanceTiming(networkTimeInMsOrTimings, serverTimeInMs, transferTimeInMs, domProcessingTimeInMs, domCompletionTimeInMs, onloadTimeInMs) {
      let networkTimeInMs;
      if (typeof networkTimeInMsOrTimings === 'object' && !!networkTimeInMsOrTimings) {
        networkTimeInMs = networkTimeInMsOrTimings.networkTimeInMs;
        serverTimeInMs = networkTimeInMsOrTimings.serverTimeInMs;
        transferTimeInMs = networkTimeInMsOrTimings.transferTimeInMs;
        domProcessingTimeInMs = networkTimeInMsOrTimings.domProcessingTimeInMs;
        domCompletionTimeInMs = networkTimeInMsOrTimings.domCompletionTimeInMs;
        onloadTimeInMs = networkTimeInMsOrTimings.onloadTimeInMs;
      } else {
        networkTimeInMs = networkTimeInMsOrTimings;
      }
      this.delegate.push(['setPagePerformanceTiming', networkTimeInMs, serverTimeInMs, transferTimeInMs, domProcessingTimeInMs, domCompletionTimeInMs, onloadTimeInMs]);
    }
    getCustomPagePerformanceTiming() {
      return this.delegate.get('getCustomPagePerformanceTiming');
    }
    /**
     * Appends a custom string to the end of the HTTP request to matomo.php.
     *
     * @param appendToUrl String to append to the end of the HTTP request to matomo.php.
     */
    appendToTrackingUrl(appendToUrl) {
      this.delegate.push(['appendToTrackingUrl', appendToUrl]);
    }
    /** Set to `true` to not track users who opt out of tracking using <i>Do Not Track</i> setting */
    setDoNotTrack(doNotTrack) {
      this.delegate.push(['setDoNotTrack', doNotTrack]);
    }
    /**
     * Enables a frame-buster to prevent the tracked web page from being framed/iframed.
     */
    killFrame() {
      this.delegate.push(['killFrame']);
    }
    /**
     * Forces the browser to load the live URL if the tracked web page is loaded from a local file
     * (e.g., saved to someone's desktop).
     *
     * @param url URL to track instead of file:// URLs.
     */
    redirectFile(url) {
      this.delegate.push(['redirectFile', url]);
    }
    /**
     * Records how long the page has been viewed if the minimumVisitLength is attained;
     * the heartBeatDelay determines how frequently to update the server.
     *
     * @param minimumVisitLength Duration before notifying the server for the duration of the visit to a page.
     * @param heartBeatDelay Delay, in seconds, between two updates to the server.
     */
    setHeartBeatTimer(minimumVisitLength, heartBeatDelay) {
      this.delegate.push(['setHeartBeatTimer', minimumVisitLength, heartBeatDelay]);
    }
    /**
     * Returns the 16 characters ID for the visitor.
     *
     * @returns Promise for the the 16 characters ID for the visitor.
     */
    getVisitorId() {
      return this.delegate.get('getVisitorId');
    }
    /**
     * Set the 16 characters ID for the visitor
     * <p/>
     * The visitorId needs to be a 16 digit hex string.
     * It won't be persisted in a cookie and needs to be set on every new page load.
     *
     * @param visitorId a 16 digit hex string
     */
    setVisitorId(visitorId) {
      this.delegate.push(['setVisitorId', visitorId]);
    }
    /**
     * Returns the visitor cookie contents in an array.
     *
     * @returns Promise for the cookie contents in an array.
     *
     * TODO better return type
     */
    getVisitorInfo() {
      return this.delegate.get('getVisitorInfo');
    }
    /**
     * Returns the visitor attribution array (Referer information and/or Campaign name & keyword).<br />
     * Attribution information is used by Matomo to credit the correct referrer (first or last referrer)
     * used when a user triggers a goal conversion.
     *
     * @returns Promise for the visitor attribution array (Referer information and/or Campaign name & keyword).
     */
    getAttributionInfo() {
      return this.delegate.get('getAttributionInfo');
    }
    /**
     * Returns the attribution campaign name.
     *
     * @returns Promise for the the attribution campaign name.
     */
    getAttributionCampaignName() {
      return this.delegate.get('getAttributionCampaignName');
    }
    /**
     * Returns the attribution campaign keyword.
     *
     * @returns Promise for the attribution campaign keyword.
     */
    getAttributionCampaignKeyword() {
      return this.delegate.get('getAttributionCampaignKeyword');
    }
    /**
     * Returns the attribution referrer timestamp.
     *
     * @returns Promise for the attribution referrer timestamp (as string).
     */
    getAttributionReferrerTimestamp() {
      return this.delegate.get('getAttributionReferrerTimestamp');
    }
    /**
     * Returns the attribution referrer URL.
     *
     * @returns Promise for the attribution referrer URL
     */
    getAttributionReferrerUrl() {
      return this.delegate.get('getAttributionReferrerUrl');
    }
    /**
     * Returns the User ID string if it was set.
     *
     * @returns Promise for the User ID for the visitor.
     */
    getUserId() {
      return this.delegate.get('getUserId');
    }
    /**
     * Set a User ID to this user (such as an email address or a username).
     *
     * @param userId User ID to set for the current visitor.
     */
    setUserId(userId) {
      this.delegate.push(['setUserId', userId]);
    }
    /**
     * Reset the User ID which also generates a new Visitor ID.
     *
     */
    resetUserId() {
      this.delegate.push(['resetUserId']);
    }
    /**
     * Override PageView id for every use of logPageView() <b>THIS SHOULD PROBABLY NOT BE CALLED IN A SINGLE-PAGE APP!</b>
     *
     * Do not use this if you call trackPageView() multiple times during tracking (e.g. when tracking a single page application)
     *
     * @param pageView
     */
    setPageViewId(pageView) {
      this.delegate.push(['setPageViewId', pageView]);
    }
    /**
     * Returns the PageView id. If not set manually using setPageViewId, this method will return the dynamic PageView id, used in the last tracked page view, or undefined if no page view was tracked yet
     */
    getPageViewId() {
      return this.delegate.get('getPageViewId');
    }
    setCustomData(...args) {
      this.delegate.push(['setCustomData', ...args]);
    }
    /**
     * Retrieves custom data.
     *
     * @returns Promise for the value of custom data.
     */
    getCustomData() {
      return this.delegate.get('getCustomData');
    }
    /**
     * Set a custom variable.
     *
     * @param index Index, the number from 1 to 5 where this custom variable name is stored for the current page view.
     * @param name Name, the name of the variable, for example: Category, Sub-category, UserType.
     * @param value Value, for example: "Sports", "News", "World", "Business"…
     * @param scope Scope of the custom variable:<br />
     * - "page" means the custom variable applies to the current page view.
     * - "visit" means the custom variable applies to the current visitor.
     */
    setCustomVariable(index, name, value, scope) {
      this.delegate.push(['setCustomVariable', index, name, value, scope]);
    }
    /**
     * Deletes a custom variable.
     *
     * @param index Index of the custom variable to delete.
     * @param scope Scope of the custom variable to delete.
     */
    deleteCustomVariable(index, scope) {
      this.delegate.push(['deleteCustomVariable', index, scope]);
    }
    /**
     * Deletes all custom variables.
     *
     * @param scope Scope of the custom variables to delete.
     */
    deleteCustomVariables(scope) {
      this.delegate.push(['deleteCustomVariables', scope]);
    }
    /**
     * Retrieves a custom variable.
     *
     * @param index Index of the custom variable to retrieve.
     * @param scope Scope of the custom variable to retrieve.
     * @returns Promise for the value of custom variable.
     */
    getCustomVariable(index, scope) {
      return this.delegate.pushFn(matomo => matomo.getCustomVariable(index, scope));
    }
    /**
     * When called then the Custom Variables of scope "visit" will be stored (persisted) in a first party cookie
     * for the duration of the visit.<br />
     * This is useful if you want to call getCustomVariable later in the visit.<br />
     * (by default custom variables are not stored on the visitor's computer.)
     *
     */
    storeCustomVariablesInCookie() {
      this.delegate.push(['storeCustomVariablesInCookie']);
    }
    /**
     * Set a custom dimension.<br />
     * (requires Matomo 2.15.1 + Custom Dimensions plugin)
     *
     * @param customDimensionId ID of the custom dimension to set.
     * @param customDimensionValue Value to be set.
     */
    setCustomDimension(customDimensionId, customDimensionValue) {
      this.delegate.push(['setCustomDimension', customDimensionId, customDimensionValue]);
    }
    /**
     * Deletes a custom dimension.<br />
     * (requires Matomo 2.15.1 + Custom Dimensions plugin)
     *
     * @param customDimensionId ID of the custom dimension to delete.
     */
    deleteCustomDimension(customDimensionId) {
      this.delegate.push(['deleteCustomDimension', customDimensionId]);
    }
    /**
     * Retrieve a custom dimension.<br />
     * (requires Matomo 2.15.1 + Custom Dimensions plugin)
     *
     * @param customDimensionId ID of the custom dimension to retrieve.
     * @return Promise for the value for the requested custom dimension.
     */
    getCustomDimension(customDimensionId) {
      return this.delegate.pushFn(matomo => matomo.getCustomDimension(customDimensionId));
    }
    /**
     * Set campaign name parameter(s).
     *
     * @param name Name of the campaign
     */
    setCampaignNameKey(name) {
      this.delegate.push(['setCampaignNameKey', name]);
    }
    /**
     * Set campaign keyword parameter(s).
     *
     * @param keyword Keyword parameter(s) of the campaign.
     */
    setCampaignKeywordKey(keyword) {
      this.delegate.push(['setCampaignKeywordKey', keyword]);
    }
    /**
     * Set to true to attribute a conversion to the first referrer.<br />
     * By default, conversion is attributed to the most recent referrer.
     *
     * @param conversionToFirstReferrer If true, Matomo will attribute the Goal conversion to the first referrer used
     * instead of the last one.
     */
    setConversionAttributionFirstReferrer(conversionToFirstReferrer) {
      this.delegate.push(['setConversionAttributionFirstReferrer', conversionToFirstReferrer]);
    }
    setEcommerceView(productOrSKU, productName, productCategory, price) {
      if (isECommerceCategoryView(productOrSKU)) {
        this.delegate.push(['setEcommerceView', false, false, productOrSKU.productCategory]);
      } else if (isECommerceItemView(productOrSKU)) {
        this.delegate.push(['setEcommerceView', productOrSKU.productSKU, productOrSKU.productName, productOrSKU.productCategory, productOrSKU.price]);
      } else {
        this.delegate.push(['setEcommerceView', productOrSKU, productName, productCategory, price]);
      }
    }
    addEcommerceItem(productOrSKU, productName, productCategory, price, quantity) {
      if (typeof productOrSKU === 'string') {
        this.delegate.push(['addEcommerceItem', productOrSKU, productName, productCategory, price, quantity]);
      } else {
        this.delegate.push(['addEcommerceItem', productOrSKU.productSKU, productOrSKU.productName, productOrSKU.productCategory, productOrSKU.price, productOrSKU.quantity]);
      }
    }
    /**
     * Remove the specified product from the untracked ecommerce order
     *
     * @param productSKU SKU of the product to remove.
     */
    removeEcommerceItem(productSKU) {
      this.delegate.push(['removeEcommerceItem', productSKU]);
    }
    /**
     * Remove all products in the untracked ecommerce order
     *
     * Note: This is done automatically after {@link #trackEcommerceOrder trackEcommerceOrder()} is called
     */
    clearEcommerceCart() {
      this.delegate.push(['clearEcommerceCart']);
    }
    /**
     * Return all ecommerce items currently in the untracked ecommerce order
     * <p/>
     * The returned array will be a copy, so changing it won't affect the ecommerce order.
     * To affect what gets tracked, use the {@link #addEcommerceItem addEcommerceItem()}, {@link #removeEcommerceItem removeEcommerceItem()},
     * {@link #clearEcommerceCart clearEcommerceCart()} methods.
     * Use this method to see what will be tracked before you track an order or cart update.
     */
    getEcommerceItems() {
      return this.delegate.get('getEcommerceItems');
    }
    /**
     * Tracks a shopping cart.<br />
     * Call this function every time a user is adding, updating or deleting a product from the cart.
     *
     * @param grandTotal Grand total of the shopping cart.
     */
    trackEcommerceCartUpdate(grandTotal) {
      this.delegate.push(['trackEcommerceCartUpdate', grandTotal]);
    }
    /**
     * Tracks an Ecommerce order, including any eCommerce item previously added to the order.<br />
     * orderId and grandTotal (ie.revenue) are required parameters.
     *
     * @param orderId ID of the tracked order.
     * @param grandTotal Grand total of the tracked order.
     * @param [subTotal] Sub total of the tracked order.
     * @param [tax] Taxes for the tracked order.
     * @param [shipping] Shipping fees for the tracked order.
     * @param [discount] Discount granted for the tracked order.
     */
    trackEcommerceOrder(orderId, grandTotal, subTotal, tax, shipping, discount) {
      this.delegate.push(['trackEcommerceOrder', orderId, grandTotal, subTotal, tax, shipping, discount]);
    }
    /**
     * Require nothing is tracked until a user consents
     *
     * By default the Matomo tracker assumes consent to tracking.
     *
     * @see `requireConsent` module configuration property
     */
    requireConsent() {
      this.delegate.push(['requireConsent']);
    }
    /**
     * Mark that the current user has consented
     *
     * The consent is one-time only, so in a subsequent browser session, the user will have to consent again.
     * To remember consent, see {@link rememberConsentGiven}.
     */
    setConsentGiven() {
      this.delegate.push(['setConsentGiven']);
    }
    /**
     * Mark that the current user has consented, and remembers this consent through a browser cookie.
     *
     * The next time the user visits the site, Matomo will remember that they consented, and track them.
     * If you call this method, you do not need to call {@link setConsentGiven}.
     *
     * @param hoursToExpire After how many hours the consent should expire. By default the consent is valid
     *                          for 30 years unless cookies are deleted by the user or the browser prior to this
     */
    rememberConsentGiven(hoursToExpire) {
      this.delegate.push(['rememberConsentGiven', hoursToExpire]);
    }
    /**
     * Remove a user's consent, both if the consent was one-time only and if the consent was remembered.
     *
     * After calling this method, the user will have to consent again in order to be tracked.
     */
    forgetConsentGiven() {
      this.delegate.push(['forgetConsentGiven']);
    }
    /** Return whether the current visitor has given consent previously or not */
    hasRememberedConsent() {
      return this.delegate.get('hasRememberedConsent');
    }
    /**
     * If consent was given, returns the timestamp when the visitor gave consent
     *
     * Only works if {@link rememberConsentGiven} was used and not when {@link setConsentGiven} was used.
     * The timestamp is the local timestamp which depends on the visitors time.
     */
    getRememberedConsent() {
      return this.delegate.get('getRememberedConsent');
    }
    /** Return whether {@link requireConsent} was called previously */
    isConsentRequired() {
      return this.delegate.get('isConsentRequired');
    }
    /**
     * Require no cookies are used
     *
     * By default the Matomo tracker assumes consent to using cookies
     */
    requireCookieConsent() {
      this.delegate.push(['requireCookieConsent']);
    }
    /**
     * Mark that the current user has consented to using cookies
     *
     * The consent is one-time only, so in a subsequent browser session, the user will have to consent again.
     * To remember cookie consent, see {@link rememberCookieConsentGiven}.
     */
    setCookieConsentGiven() {
      this.delegate.push(['setCookieConsentGiven']);
    }
    /**
     * Mark that the current user has consented to using cookies, and remembers this consent through a browser cookie.
     *
     * The next time the user visits the site, Matomo will remember that they consented, and use cookies.
     * If you call this method, you do not need to call {@link setCookieConsentGiven}.
     *
     * @param hoursToExpire After how many hours the cookie consent should expire. By default the consent is valid
     *                          for 30 years unless cookies are deleted by the user or the browser prior to this
     */
    rememberCookieConsentGiven(hoursToExpire) {
      this.delegate.push(['rememberCookieConsentGiven', hoursToExpire]);
    }
    /**
     * Remove a user's cookie consent, both if the consent was one-time only and if the consent was remembered.
     *
     * After calling this method, the user will have to consent again in order for cookies to be used.
     */
    forgetCookieConsentGiven() {
      this.delegate.push(['forgetCookieConsentGiven']);
    }
    getRememberedCookieConsent() {
      return this.delegate.get('getRememberedCookieConsent');
    }
    /** Return whether cookies are currently enabled or disabled */
    areCookiesEnabled() {
      return this.delegate.get('areCookiesEnabled');
    }
    /** After calling this function, the user will be opted out and no longer be tracked */
    optUserOut() {
      this.delegate.push(['optUserOut']);
    }
    /** After calling this method the user will be tracked again */
    forgetUserOptOut() {
      this.delegate.push(['forgetUserOptOut']);
    }
    /**
     * Return whether the user is opted out or not
     *
     * Note: This method might not return the correct value if you are using the opt out iframe.
     */
    isUserOptedOut() {
      return this.delegate.get('isUserOptedOut');
    }
    /**
     * Disables all first party cookies.<br />
     * Existing Matomo cookies for this websites will be deleted on the next page view.
     */
    disableCookies() {
      this.delegate.push(['disableCookies']);
    }
    /**
     * Deletes the tracking cookies currently set (useful when creating new visits).
     */
    deleteCookies() {
      this.delegate.push(['deleteCookies']);
    }
    /**
     * Returns whether cookies are enabled and supported by this browser.
     *
     * @returns Promise for the support and activation of cookies.
     */
    hasCookies() {
      return this.delegate.get('hasCookies');
    }
    /**
     * Set the tracking cookie name prefix.<br />
     * Default prefix is 'pk'.
     *
     * @param prefix Prefix for the tracking cookie names.
     */
    setCookieNamePrefix(prefix) {
      this.delegate.push(['setCookieNamePrefix', prefix]);
    }
    /**
     * Set the domain of the tracking cookies.<br />
     * Default is the document domain.<br />
     * If your website can be visited at both www.example.com and example.com, you would use: `'.example.com'` or `'*.example.com'`.
     *
     * @param domain Domain of the tracking cookies.
     */
    setCookieDomain(domain) {
      this.delegate.push(['setCookieDomain', domain]);
    }
    /**
     * Set the path of the tracking cookies.<br />
     * Default is '/'.
     *
     * @param path Path of the tracking cookies.
     */
    setCookiePath(path) {
      this.delegate.push(['setCookiePath', path]);
    }
    /**
     * Set to true to enable the Secure cookie flag on all first party cookies.<br />
     * This should be used when your website is only available under HTTPS so that all tracking cookies are always sent
     * over secure connection.
     *
     * @param secure If true, the secure cookie flag will be set on all first party cookies.
     */
    setSecureCookie(secure) {
      this.delegate.push(['setSecureCookie', secure]);
    }
    /**
     * Set cookie <i>same site</i>
     * <p/>
     * Defaults to Lax.
     * Can be set to None or Strict.
     * None requires all traffic to be on HTTPS and will also automatically set the secure cookie.
     * It can be useful for example if the tracked website is an iframe.
     * Strict only works if your Matomo and the website runs on the very same domain.
     */
    setCookieSameSite(sameSite) {
      this.delegate.push(['setCookieSameSite', sameSite]);
    }
    /**
     * Set the visitor cookie timeout.<br />
     * Default is 13 months.
     *
     * @param timeout Timeout, in seconds, for the visitor cookie timeout.
     */
    setVisitorCookieTimeout(timeout) {
      this.delegate.push(['setVisitorCookieTimeout', timeout]);
    }
    /**
     * Set the referral cookie timeout.<br />
     * Default is 6 months.
     *
     * @param timeout Timeout, in seconds, for the referral cookie timeout.
     */
    setReferralCookieTimeout(timeout) {
      this.delegate.push(['setReferralCookieTimeout', timeout]);
    }
    /**
     * Set the session cookie timeout.<br />
     * Default is 30 minutes.
     *
     * @param timeout Timeout, in seconds, for the session cookie timeout.
     */
    setSessionCookieTimeout(timeout) {
      this.delegate.push(['setSessionCookieTimeout', timeout]);
    }
    /**
     * Adds a click listener to a specific link element.<br />
     * When clicked, Matomo will log the click automatically.
     *
     * @param element Element on which to add a click listener.
     */
    addListener(element) {
      this.delegate.push(['addListener', element]);
    }
    /**
     * Set the request method to either "GET" or "POST". (The default is "GET".)<br />
     * To use the POST request method, either:<br />
     * 1) the Matomo host is the same as the tracked website host (Matomo installed in the same domain as your tracked website), or<br />
     * 2) if Matomo is not installed on the same host as your website, you need to enable CORS (Cross domain requests).
     *
     * @param method HTTP method for sending information to the Matomo server.
     */
    setRequestMethod(method) {
      this.delegate.push(['setRequestMethod', method]);
    }
    /**
     * Set a function that will process the request content.<br />
     * The function will be called once the request (query parameters string) has been prepared, and before the request content is sent.
     *
     * @param callback Function that will process the request content.
     */
    setCustomRequestProcessing(callback) {
      this.delegate.push(['setCustomRequestProcessing', callback]);
    }
    /**
     * Set request Content-Type header value.<br />
     * Applicable when "POST" request method is used via setRequestMethod.
     *
     * @param contentType Value for Content-Type HTTP header.
     */
    setRequestContentType(contentType) {
      this.delegate.push(['setRequestContentType', contentType]);
    }
    /**
     * Disable the feature which groups together multiple tracking requests and send them as a bulk POST request.
     * <p/>
     * Disabling this feature is useful when you want to be able to replay all logs:
     * one must use disableQueueRequest to disable this behaviour to later be able to replay logged
     * Matomo logs (otherwise a subset of the requests wouldn't be able to be replayed).
     */
    disableQueueRequest() {
      this.delegate.push(['disableQueueRequest']);
    }
    /**
     * Defines after how many ms a queued requests will be executed after the request was queued initially
     * <p/>
     * The higher the value the more tracking requests can be sent together at once
     *
     * @param interval Interval in milliseconds, must be at least 1000, defaults to 2500
     */
    setRequestQueueInterval(interval) {
      this.delegate.push(['setRequestQueueInterval', interval]);
    }
    /** Disable sending tracking requests using `navigator.sendBeacon` which is enabled by default */
    disableAlwaysUseSendBeacon() {
      this.delegate.push(['disableAlwaysUseSendBeacon']);
    }
    /** Enable sending tracking requests using `navigator.sendBeacon` (enabled by default) */
    alwaysUseSendBeacon() {
      this.delegate.push(['alwaysUseSendBeacon']);
    }
    /**
     * Enable Javascript errors tracking. JS errors are then tracked as events with category
     * "JavaScript Errors". Refer to official doc for more details.
     *
     * @see https://matomo.org/faq/how-to/how-do-i-enable-basic-javascript-error-tracking-and-reporting-in-matomo-browser-console-error-messages/
     */
    enableJSErrorTracking() {
      this.delegate.push(['enableJSErrorTracking']);
    }
    /**
     * Enable tracking of file:// protocol actions. By default, the file:// protocol is not tracked.
     */
    enableFileTracking() {
      this.delegate.push(['enableFileTracking']);
    }
    /**
     * Set array of hostnames or domains that should be ignored as referrers.
     *
     * For wildcard subdomains, you can use: `setExcludedReferrers('.example.com');` or `setExcludedReferrers('*.example.com');`.
     * You can also specify a path along a domain: `setExcludedReferrers('*.example.com/subsite1');`.
     *
     * This method is available as of Matomo 4.12.
     */
    setExcludedReferrers(...excludedReferrers) {
      const flattened = excludedReferrers.flat();
      this.delegate.push(['setExcludedReferrers', flattened]);
    }
    /**
     * Returns the list of excluded referrers, which was previously set using setExcludedReferrers
     */
    getExcludedReferrers() {
      return this.delegate.get('getExcludedReferrers');
    }
    /**
     * By default, Matomo accesses information from the visitor's browser to detect the current browser resolution
     * and what browser plugins (for example PDF and cookies) are supported.
     *
     * This information is used to show you reports on your visitor's browser resolution, supported browser plugins,
     * and it is also used to generate a short-lived identifier for every visitor which we call the config_id.
     * Some privacy regulations may only allow accessing information from a visitor's device after having consent.
     * If this applies to you, call this method to no longer access this information.
     *
     * @see https://matomo.org/faq/how-do-i-disable-browser-feature-detection-completely/
     */
    disableBrowserFeatureDetection() {
      this.delegate.push(['disableBrowserFeatureDetection']);
    }
    /** Enable the browser feature detection if you previously disabled it */
    enableBrowserFeatureDetection() {
      this.delegate.push(['enableBrowserFeatureDetection']);
    }
    /**
     * By default, Matomo will send campaign parameters (mtm, utm, etc.) to the tracker and record that information.
     * Some privacy regulations may not allow for this information to be collected.
     * If this applies to you, call this method to prevent campaign parameters from being sent to the tracker.
     *
     * <b>This method is available as of Matomo 5.1.</b>
     */
    disableCampaignParameters() {
      this.delegate.push(['disableCampaignParameters']);
    }
    static {
      this.ɵfac = function MatomoTracker_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoTracker)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MatomoTracker,
        factory: MatomoTracker.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MatomoTracker;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function coerceSiteId(siteId) {
  return `${siteId}`;
}
function buildTrackerUrl(url, suffix) {
  if (suffix == null) {
    return appendTrailingSlash(url) + DEFAULT_TRACKER_SUFFIX;
  }
  return url + suffix;
}
const DEFAULT_TRACKER_SUFFIX = 'matomo.php';
const DEFAULT_SCRIPT_SUFFIX = 'matomo.js';
function createMatomoInitializer() {
  const disabled = inject(INTERNAL_MATOMO_CONFIGURATION$1).disabled;
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  return disabled || !isBrowser ? new NoopMatomoInitializer() : new MatomoInitializerService();
}
class NoopMatomoInitializer {
  initialize() {
    // No-op
  }
  init() {
    // No-op
  }
  initializeTracker(_) {
    // No-op
  }
}
let MatomoInitializerService = /*#__PURE__*/(() => {
  class MatomoInitializerService {
    constructor() {
      this.config = inject(INTERNAL_MATOMO_CONFIGURATION$1);
      this.deferredConfig = inject(DEFERRED_INTERNAL_MATOMO_CONFIGURATION);
      this.tracker = inject(MatomoTracker);
      this.scriptInjector = inject(ScriptInjector);
      this.initialize = runOnce(() => {
        this.runPreInitTasks();
        if (isAutoConfigurationMode(this.config)) {
          this.injectMatomoScript(this.config);
        }
      }, ALREADY_INITIALIZED_ERROR);
      this.injectMatomoScript = runOnce(config => {
        if (isExplicitTrackerConfiguration(config)) {
          const {
            scriptUrl: customScriptUrl
          } = config;
          const [mainTracker, ...additionalTrackers] = getTrackersConfiguration(config);
          const scriptUrl = customScriptUrl ?? appendTrailingSlash(mainTracker.trackerUrl) + DEFAULT_SCRIPT_SUFFIX;
          this.registerMainTracker(mainTracker);
          this.registerAdditionalTrackers(additionalTrackers);
          this.scriptInjector.injectDOMScript(scriptUrl);
        } else if (isEmbeddedTrackerConfiguration(config)) {
          const {
            scriptUrl,
            trackers: additionalTrackers
          } = {
            trackers: [],
            ...config
          };
          this.registerAdditionalTrackers(additionalTrackers);
          this.scriptInjector.injectDOMScript(scriptUrl);
        }
        this.deferredConfig.markReady(config);
      }, ALREADY_INJECTED_ERROR);
      initializeMatomoHolder();
    }
    // TODO v7 remove
    /** @deprecated Will be removed in v7+. Use {@link initialize initialize()} instead. */
    init() {
      this.initialize();
    }
    initializeTracker(config) {
      this.injectMatomoScript(config);
    }
    registerMainTracker(mainTracker) {
      const mainTrackerUrl = buildTrackerUrl(mainTracker.trackerUrl, mainTracker.trackerUrlSuffix);
      const mainTrackerSiteId = coerceSiteId(mainTracker.siteId);
      this.tracker.setTrackerUrl(mainTrackerUrl);
      this.tracker.setSiteId(mainTrackerSiteId);
    }
    registerAdditionalTrackers(additionalTrackers) {
      additionalTrackers.forEach(({
        trackerUrl,
        siteId,
        trackerUrlSuffix
      }) => {
        const additionalTrackerUrl = buildTrackerUrl(trackerUrl, trackerUrlSuffix);
        const additionalTrackerSiteId = coerceSiteId(siteId);
        this.tracker.addTracker(additionalTrackerUrl, additionalTrackerSiteId);
      });
    }
    runPreInitTasks() {
      if (this.config.acceptDoNotTrack) {
        this.tracker.setDoNotTrack(true);
      }
      if (this.config.requireConsent === 'cookie') {
        this.tracker.requireCookieConsent();
      } else if (this.config.requireConsent === 'tracking') {
        this.tracker.requireConsent();
      }
      if (this.config.enableJSErrorTracking) {
        this.tracker.enableJSErrorTracking();
      }
      if (this.config.disableCampaignParameters) {
        this.tracker.disableCampaignParameters();
      }
      if (this.config.trackAppInitialLoad) {
        this.tracker.trackPageView();
      }
      if (this.config.enableLinkTracking) {
        this.tracker.enableLinkTracking(this.config.enableLinkTracking === 'enable-pseudo');
      }
    }
    static {
      this.ɵfac = function MatomoInitializerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoInitializerService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MatomoInitializerService,
        factory: () => createMatomoInitializer(),
        providedIn: 'root'
      });
    }
  }
  return MatomoInitializerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const PRIVATE_MATOMO_PROVIDERS = Symbol('MATOMO_PROVIDERS');
const PRIVATE_MATOMO_CHECKS = Symbol('MATOMO_CHECKS');
function createMatomoFeature(kind, providers, checks) {
  return {
    kind,
    [PRIVATE_MATOMO_PROVIDERS]: providers,
    [PRIVATE_MATOMO_CHECKS]: checks
  };
}
/**
 * Return Matomo providers (typically added to an application's root module)
 *
 * Additional features may be provided as additional arguments, like this:
 * @example
 * // Simple configuration
 * providers: [ provideMatomo({ siteId: 1, trackerUrl: '...' }) ]
 *
 * // With additional features
 * providers: [
 *     provideMatomo(
 *         { siteId: 1, trackerUrl: '...' },
 *         withScriptFactory(...),
 *     )
 * ]
 *
 * // With advanced config factory function
 * providers: [
 *     provideMatomo(
 *         () => inject(MyService).getMatomoConfig(),
 *         withScriptFactory(...),
 *     )
 * ]
 *
 * @param config Matomo configuration (or configuration factory, which can use `inject`)
 * @param features Optional additional features to enable
 *
 * @see MatomoConfiguration
 * @see withScriptFactory
 * @see withRouter
 * @see withRouterInterceptors
 * @see withRouteData
 */
function provideMatomo(config, ...features) {
  const providers = [{
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue() {
      inject(MatomoInitializerService).initialize();
    }
  }];
  const featuresKind = [];
  if (typeof config === 'function') {
    providers.push({
      provide: MATOMO_CONFIGURATION,
      useFactory: config
    });
  } else {
    providers.push({
      provide: MATOMO_CONFIGURATION,
      useValue: config
    });
  }
  for (const feature of features) {
    providers.push(...feature[PRIVATE_MATOMO_PROVIDERS]);
    featuresKind.push(feature.kind);
  }
  for (const feature of features) {
    feature[PRIVATE_MATOMO_CHECKS]?.(featuresKind);
  }
  return makeEnvironmentProviders(providers);
}
/** Add a custom script factory to use for Matomo's script element */
function withScriptFactory(scriptFactory) {
  return createMatomoFeature("ScriptFactory" /* CoreMatomoFeatureKind.ScriptFactory */, [{
    provide: MATOMO_SCRIPT_FACTORY,
    useValue: scriptFactory
  }]);
}
const DEFAULT_BORDER = '0';
const DEFAULT_WIDTH = '600px';
const DEFAULT_HEIGHT = '200px';
const URL_PATTERN = '{SERVER}/index.php?module=CoreAdminHome&action=optOut&language={LOCALE}&backgroundColor={BG_COLOR}&fontColor={COLOR}&fontSize={FONT_SIZE}&fontFamily={FONT_FAMILY}';
function missingServerUrlError() {
  return new Error(`It is required to set [serverUrl] when Matomo configuration mode is set to 'manual'`);
}
/**
 * Basic opt-out form, based on an iframe auto-generated by Matomo.
 *
 * <b>WARNING:</b> By default, this component assumes the tracker url set in MatomoConfiguration is
 * safe to be used as an iframe `src`. You have to make sure that this url is safe before using this
 * component!
 *
 * Note: This component relies on the matomo-generated opt-out form, which is deprecated
 * since Matomo version 4.12.0. It will be marked as deprecated soon.
 *
 * @see https://developer.matomo.org/changelog#new-privacy-opt-out-options
 */
let MatomoOptOutFormComponent = /*#__PURE__*/(() => {
  class MatomoOptOutFormComponent {
    constructor(sanitizer, config, locale = '') {
      this.sanitizer = sanitizer;
      this.config = config;
      this._defaultServerUrlInitialized = false;
      this._border = DEFAULT_BORDER;
      this._width = DEFAULT_WIDTH;
      this._height = DEFAULT_HEIGHT;
      this._iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl('');
      /** Font color (note that Matomo currently only supports hexadecimal without leading hash notation) */
      this.color = '';
      /** Background color (note that Matomo currently only supports hexadecimal without leading hash notation) */
      this.backgroundColor = '';
      this.fontSize = '';
      this.fontFamily = '';
      // Set default locale
      this.locale = locale;
    }
    get serverUrl() {
      return this._serverUrlOverride;
    }
    /**
     * Set a custom Matomo server url to be used for iframe generation
     * <br>
     * By default, tracker url is retrieved from MatomoConfiguration.
     * <br>
     * <b>WARNING:</b> This component assumes the url you provide is safe to be used as an iframe
     * `src`. You have to make sure that this url is safe before using this component!
     */
    set serverUrl(value) {
      this._serverUrlOverride = value;
    }
    get iframeSrc() {
      return this._iframeSrc;
    }
    get height() {
      return this._height;
    }
    set height(value) {
      this._height = coerceCssSizeBinding(value);
    }
    get width() {
      return this._width;
    }
    set width(value) {
      this._width = coerceCssSizeBinding(value);
    }
    get border() {
      return this._border;
    }
    set border(value) {
      this._border = coerceCssSizeBinding(value);
    }
    ngOnInit() {
      this.updateUrl();
      this.config.then(config => {
        if (isAutoConfigurationMode(config) && isExplicitTrackerConfiguration(config)) {
          this._defaultServerUrl = getTrackersConfiguration(config)[0].trackerUrl;
        }
        this._defaultServerUrlInitialized = true;
        this.updateUrl();
      });
    }
    ngOnChanges(changes) {
      if ('serverUrl' in changes || 'locale' in changes || 'color' in changes || 'backgroundColor' in changes || 'fontSize' in changes || 'fontFamily' in changes) {
        this.updateUrl();
      }
    }
    updateUrl() {
      let serverUrl = this._defaultServerUrl;
      if (this._serverUrlOverride) {
        serverUrl = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, this._serverUrlOverride);
      }
      if (!serverUrl) {
        if (this._defaultServerUrlInitialized) {
          throw missingServerUrlError();
        } else {
          return;
        }
      }
      const url = URL_PATTERN.replace('{SERVER}', serverUrl).replace('{LOCALE}', encodeURIComponent(this.locale)).replace('{COLOR}', encodeURIComponent(this.color)).replace('{BG_COLOR}', encodeURIComponent(this.backgroundColor)).replace('{FONT_SIZE}', encodeURIComponent(this.fontSize)).replace('{FONT_FAMILY}', encodeURIComponent(this.fontFamily));
      this._iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    static {
      this.ɵfac = function MatomoOptOutFormComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoOptOutFormComponent)(i0.ɵɵdirectiveInject(i1.DomSanitizer), i0.ɵɵdirectiveInject(ASYNC_INTERNAL_MATOMO_CONFIGURATION), i0.ɵɵdirectiveInject(LOCALE_ID, 8));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: MatomoOptOutFormComponent,
        selectors: [["matomo-opt-out-form"]],
        inputs: {
          locale: "locale",
          color: "color",
          backgroundColor: "backgroundColor",
          fontSize: "fontSize",
          fontFamily: "fontFamily",
          serverUrl: "serverUrl",
          height: "height",
          width: "width",
          border: "border"
        },
        features: [i0.ɵɵNgOnChangesFeature],
        decls: 1,
        vars: 7,
        consts: [[3, "src"]],
        template: function MatomoOptOutFormComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelement(0, "iframe", 0);
          }
          if (rf & 2) {
            i0.ɵɵstyleProp("border", ctx.border)("height", ctx.height)("width", ctx.width);
            i0.ɵɵproperty("src", ctx.iframeSrc, i0.ɵɵsanitizeResourceUrl);
          }
        },
        encapsulation: 2
      });
    }
  }
  return MatomoOptOutFormComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let MatomoTrackClickDirective = /*#__PURE__*/(() => {
  class MatomoTrackClickDirective {
    constructor(tracker) {
      this.tracker = tracker;
    }
    onClick() {
      this.tracker.trackEvent(requireNonNull(this.matomoClickCategory, 'matomo category is required'), requireNonNull(this.matomoClickAction, 'matomo action is required'), this.matomoClickName, this.matomoClickValue);
    }
    static {
      this.ɵfac = function MatomoTrackClickDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoTrackClickDirective)(i0.ɵɵdirectiveInject(MatomoTracker));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatomoTrackClickDirective,
        selectors: [["", "matomoClickCategory", "", "matomoClickAction", ""]],
        hostBindings: function MatomoTrackClickDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function MatomoTrackClickDirective_click_HostBindingHandler() {
              return ctx.onClick();
            });
          }
        },
        inputs: {
          matomoClickCategory: "matomoClickCategory",
          matomoClickAction: "matomoClickAction",
          matomoClickName: "matomoClickName",
          matomoClickValue: "matomoClickValue"
        }
      });
    }
  }
  return MatomoTrackClickDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function coerceEventNames(input) {
  if (input && input.length > 0) {
    return Array.isArray(input) ? input : [input];
  } else {
    return undefined;
  }
}
let MatomoTrackerDirective = /*#__PURE__*/(() => {
  class MatomoTrackerDirective {
    constructor(tracker, elementRef) {
      this.tracker = tracker;
      this.elementRef = elementRef;
    }
    /** Track a Matomo event whenever specified DOM event is triggered */
    set matomoTracker(input) {
      const eventNames = coerceEventNames(input);
      this.sub?.unsubscribe();
      if (eventNames) {
        const handlers = eventNames.map(eventName => fromEvent(this.elementRef.nativeElement, eventName));
        this.sub = merge(...handlers).subscribe(() => this.trackEvent());
      } else {
        this.sub = undefined;
      }
    }
    ngOnDestroy() {
      this.sub?.unsubscribe();
    }
    trackEvent(arg1, arg2) {
      let category = this.matomoCategory;
      let action = this.matomoAction;
      let name = this.matomoName;
      let value = this.matomoValue;
      if (typeof arg1 === 'object') {
        category = arg1.category ?? category;
        action = arg1.action ?? action;
        name = arg1.name ?? name;
        value = arg1.value ?? value;
      } else if (typeof arg1 === 'string') {
        name = arg1;
        if (typeof arg2 === 'number') {
          value = arg2;
        }
      } else if (typeof arg1 === 'number') {
        value = arg1;
      }
      this.tracker.trackEvent(requireNonNull(category, 'matomo category is required'), requireNonNull(action, 'matomo action is required'), name, value);
    }
    static {
      this.ɵfac = function MatomoTrackerDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoTrackerDirective)(i0.ɵɵdirectiveInject(MatomoTracker), i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatomoTrackerDirective,
        selectors: [["", "matomoTracker", ""]],
        inputs: {
          matomoCategory: "matomoCategory",
          matomoAction: "matomoAction",
          matomoName: "matomoName",
          matomoValue: "matomoValue",
          matomoTracker: "matomoTracker"
        },
        exportAs: ["matomo"]
      });
    }
  }
  return MatomoTrackerDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const MATOMO_DIRECTIVES = [MatomoTrackerDirective, MatomoTrackClickDirective, MatomoOptOutFormComponent];
function buildProviders(config, scriptFactory) {
  const features = [];
  if (scriptFactory) {
    features.push(withScriptFactory(scriptFactory));
  }
  return provideMatomo(config, ...features);
}
let MatomoModule = /*#__PURE__*/(() => {
  class MatomoModule {
    static forRoot(config, scriptFactory) {
      return {
        ngModule: MatomoModule,
        providers: [buildProviders(config, scriptFactory)]
      };
    }
    static {
      this.ɵfac = function MatomoModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: MatomoModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return MatomoModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * @deprecated use MatomoModule instead
 * @breaking-change 7.0.0
 */
let NgxMatomoModule = /*#__PURE__*/(() => {
  class NgxMatomoModule {
    static forRoot(config, scriptFactory) {
      return {
        ngModule: NgxMatomoModule,
        providers: [buildProviders(config, scriptFactory)]
      };
    }
    static {
      this.ɵfac = function NgxMatomoModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxMatomoModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgxMatomoModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [MatomoModule, MatomoModule]
      });
    }
  }
  return NgxMatomoModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * @deprecated use MatomoModule instead
 * @breaking-change 7.0.0
 */
let NgxMatomoTrackerModule = /*#__PURE__*/(() => {
  class NgxMatomoTrackerModule {
    static forRoot(config, scriptFactory) {
      return {
        ngModule: NgxMatomoTrackerModule,
        providers: [buildProviders(config, scriptFactory)]
      };
    }
    static {
      this.ɵfac = function NgxMatomoTrackerModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxMatomoTrackerModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgxMatomoTrackerModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [NgxMatomoModule, NgxMatomoModule]
      });
    }
  }
  return NgxMatomoTrackerModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of tracker
 */
/**
 * @deprecated for internal use only
 * @breaking-change 6.0.0
 */
const INTERNAL_MATOMO_CONFIGURATION = INTERNAL_MATOMO_CONFIGURATION$1;

/**
 * Generated bundle index. Do not edit.
 */

export { INTERNAL_MATOMO_CONFIGURATION, MATOMO_CONFIGURATION, MATOMO_DIRECTIVES, MATOMO_SCRIPT_FACTORY, MatomoConsentMode, MatomoInitializationMode, MatomoInitializerService, MatomoModule, MatomoOptOutFormComponent, MatomoTrackClickDirective, MatomoTracker, MatomoTrackerDirective, NgxMatomoModule, NgxMatomoTrackerModule, createDefaultMatomoScriptElement, provideMatomo, withScriptFactory, ASYNC_INTERNAL_MATOMO_CONFIGURATION as ɵASYNC_INTERNAL_MATOMO_CONFIGURATION, DEFERRED_INTERNAL_MATOMO_CONFIGURATION as ɵDEFERRED_INTERNAL_MATOMO_CONFIGURATION, INTERNAL_MATOMO_CONFIGURATION$1 as ɵINTERNAL_MATOMO_CONFIGURATION, InternalMatomoTracker as ɵInternalMatomoTracker, MATOMO_ROUTER_ENABLED as ɵMATOMO_ROUTER_ENABLED, ScriptInjector as ɵScriptInjector, appendTrailingSlash as ɵappendTrailingSlash, createMatomoFeature as ɵcreateMatomoFeature, getTrackersConfiguration as ɵgetTrackersConfiguration, isAutoConfigurationMode as ɵisAutoConfigurationMode, isExplicitTrackerConfiguration as ɵisExplicitTrackerConfiguration, runOnce as ɵrunOnce };
